/**
 * ===========================================================================
 * CODE 128 (subset B) — the encoder, and two ways to draw it
 * ===========================================================================
 *
 * This lived inside `unitLabelPrint.ts`, which prints a Rollo label. It is a
 * general thing — the module widths of a Code 128 barcode — and the orders
 * screen now needs the same bars ON SCREEN, so it lives here and that file
 * imports it rather than the two carrying separate copies of a pattern table
 * where a single wrong digit produces a barcode that scans as the wrong id.
 *
 * super-admin-ui has neither `jsbarcode` nor `jspdf` (platform-app has both;
 * this app has neither), and the working rules forbid adding packages — hence
 * hand-rolled. Subset B covers ASCII 32-127, which is everything an
 * `RTA-######` tracking id, an `RB-#######` order number or a serial needs.
 */

// Public-domain pattern table (bar/space module widths, 0-105 + STOP), the
// same one JsBarcode itself ships.
const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232',
];
const CODE128_STOP = '2331112';
const CODE128_START_B = 104;

/** Full bar-width pattern for `text`, including start/checksum/stop, subset B only (ASCII 32-127). */
export function encodeCode128B(text: string): string {
  const values: number[] = [CODE128_START_B];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const val = code - 32;
    if (val < 0 || val > 94) continue; // outside subset B — skip rather than corrupt the whole barcode
    values.push(val);
  }
  let checksum = values[0];
  for (let i = 1; i < values.length; i++) checksum += values[i] * i;
  values.push(checksum % 103);

  let pattern = '';
  for (const v of values) pattern += CODE128_PATTERNS[v];
  return pattern + CODE128_STOP;
}

export interface BarcodeBar {
  /** Left edge, in MODULES from the start of the symbol. */
  x: number;
  /** Width in modules. */
  width: number;
}

export interface BarcodeGeometry {
  bars: BarcodeBar[];
  /** Total symbol width in modules — the viewBox width for an SVG drawing. */
  modules: number;
}

/**
 * The BARS ONLY, in module units, for a caller to scale however it likes.
 *
 * Module units rather than pixels because the same symbol is drawn at 90px on a
 * label and at 26px in a table row; handing back geometry lets an SVG scale it
 * with a `viewBox` and stay crisp at both, which a rasterised canvas cannot.
 *
 * An empty or unencodable `text` yields no bars, so a caller renders nothing
 * rather than a short meaningless symbol that would scan as garbage.
 */
export function code128Geometry(text: string): BarcodeGeometry {
  const clean = String(text || '').trim();
  if (!clean) return { bars: [], modules: 0 };

  const pattern = encodeCode128B(clean);
  const bars: BarcodeBar[] = [];
  let x = 0;
  let isBar = true;
  for (const ch of pattern) {
    const w = Number(ch);
    if (isBar) bars.push({ x, width: w });
    x += w;
    isBar = !isBar;
  }
  return { bars, modules: x };
}

/**
 * The same symbol on a canvas, for the print pipeline — which embeds a data
 * URL and cannot use an SVG element.
 */
export function drawCode128(text: string, widthPx: number, heightPx: number): HTMLCanvasElement {
  const { bars, modules } = code128Geometry(text);
  const unit = modules > 0 ? widthPx / modules : widthPx;

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.fillStyle = '#000';
  for (const bar of bars) {
    ctx.fillRect(bar.x * unit, 0, Math.max(1, bar.width * unit), heightPx);
  }
  return canvas;
}
